import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays } from 'lucide-react';
import './DatePicker.scss';

const DAYS_SK = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];
const MONTHS_SK = [
  'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
  'Júl', 'August', 'September', 'Október', 'November', 'December',
];

const DROPDOWN_HEIGHT = 360;
const DROPDOWN_OFFSET = 6;

function isValidDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function toValidDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === 'string') {
    const isoOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoOnlyMatch) {
      const y = Number(isoOnlyMatch[1]);
      const m = Number(isoOnlyMatch[2]);
      const d = Number(isoOnlyMatch[3]);
      const local = new Date(y, m - 1, d);
      return isValidDate(local) ? local : null;
    }
    const parsed = new Date(value);
    return isValidDate(parsed) ? parsed : null;
  }

  if (typeof value === 'number') {
    const parsed = new Date(value);
    return isValidDate(parsed) ? parsed : null;
  }

  if (typeof value === 'object' && value.target?.value) {
    return toValidDate(value.target.value);
  }

  return null;
}

function DatePicker({
  label,
  value,
  onChange,
  placeholder = 'Vyber dátum...',
  disabled = false,
  minDate,
  maxDate,
  error,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [portalStyle, setPortalStyle] = useState({ position: 'fixed', top: 0, left: 0, zIndex: 9999 });
  const [viewDate, setViewDate] = useState(() => {
    const parsed = toValidDate(value);
    if (parsed) return parsed;
    return new Date();
  });
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const parsed = toValidDate(value);
    if (parsed) setViewDate(parsed);
  }, [value]);

  const calculatePosition = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const shouldOpenUpward = spaceBelow < DROPDOWN_HEIGHT + DROPDOWN_OFFSET && rect.top > spaceBelow;

    setOpenUpward(shouldOpenUpward);
    setPortalStyle({
      position: 'fixed',
      left: rect.left,
      zIndex: 9999,
      ...(shouldOpenUpward
        ? { bottom: window.innerHeight - rect.top + DROPDOWN_OFFSET }
        : { top: rect.bottom + DROPDOWN_OFFSET }),
    });
  };

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) calculatePosition();
    setIsOpen((prev) => !prev);
  };

  // Close on outside click – covers both the trigger and the portalled dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  // Reposition on scroll / resize while open (e.g. scrolling inside a modal body)
  useEffect(() => {
    if (!isOpen) return;
    const update = () => calculatePosition();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const days = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    // Monday = 0
    let startDow = (firstDay.getDay() + 6) % 7;
    const result = [];

    // Previous month days
    for (let i = startDow - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      result.push({ date: d, outside: true });
    }
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      result.push({ date: new Date(year, month, i), outside: false });
    }
    // Next month fill to 42
    const remaining = 42 - result.length;
    for (let i = 1; i <= remaining; i++) {
      result.push({ date: new Date(year, month + 1, i), outside: true });
    }
    return result;
  }, [year, month]);

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    const selected = toValidDate(value);
    if (!selected) return false;
    return date.toDateString() === selected.toDateString();
  };

  const isDisabled = (date) => {
    const min = toValidDate(minDate);
    const max = toValidDate(maxDate);
    if (min && date < min) return true;
    if (max && date > max) return true;
    return false;
  };

  const handleSelect = (date) => {
    if (isDisabled(date)) return;
    onChange?.(date);
    setIsOpen(false);
  };

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const handleToday = () => {
    const today = new Date();
    setViewDate(today);
    handleSelect(today);
  };

  const handleClear = () => {
    onChange?.(null);
    setIsOpen(false);
  };

  const formatDate = (date) => {
    const d = toValidDate(date);
    if (!d) return '';
    return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  };

  return (
    <div className={`datepicker ${className}`} ref={ref}>
      {label && <label className="datepicker__label">{label}</label>}

      <div className="datepicker__input-wrapper">
        <input
          type="text"
          className={`datepicker__input ${disabled ? 'datepicker__input--disabled' : ''} ${error ? 'datepicker__input--error' : ''}`}
          value={value ? formatDate(value) : ''}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          onClick={handleToggle}
        />
        <span className="datepicker__icon"><CalendarDays size={16} /></span>
      </div>

      {createPortal(
        <div
          ref={dropdownRef}
          className={`datepicker__dropdown ${isOpen ? 'datepicker__dropdown--open' : ''} ${openUpward ? 'datepicker__dropdown--upward' : ''}`}
          style={portalStyle}
        >
          <div className="datepicker__header">
            <button className="datepicker__nav-btn" onClick={handlePrevMonth}>‹</button>
            <span className="datepicker__month-year">{MONTHS_SK[month]} {year}</span>
            <button className="datepicker__nav-btn" onClick={handleNextMonth}>›</button>
          </div>

          <div className="datepicker__weekdays">
            {DAYS_SK.map((d) => (
              <span key={d} className="datepicker__weekday">{d}</span>
            ))}
          </div>

          <div className="datepicker__days">
            {days.map(({ date, outside }, i) => (
              <button
                key={i}
                type="button"
                className={`datepicker__day ${outside ? 'datepicker__day--outside' : ''} ${isToday(date) ? 'datepicker__day--today' : ''} ${isSelected(date) ? 'datepicker__day--selected' : ''} ${isDisabled(date) ? 'datepicker__day--disabled' : ''}`}
                onClick={() => !outside && handleSelect(date)}
                disabled={outside || isDisabled(date)}
              >
                {date.getDate()}
              </button>
            ))}
          </div>

          <div className="datepicker__footer">
            <button type="button" className="datepicker__today-btn" onClick={handleToday}>Dnes</button>
            <button type="button" className="datepicker__clear-btn" onClick={handleClear}>Vymazať</button>
          </div>
        </div>,
        document.body
      )}

      {error && <span className="datepicker__error">{error}</span>}
    </div>
  );
}

export default DatePicker;
